import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { getOptionsHistoricalData } from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
  Area,
  ComposedChart,
  Bar,
} from 'recharts';
import StandardLoader from '../StandardLoader';

/**
 * OptionsChartWidget - Displays historical premium price chart for options contracts
 *
 * Props:
 * - widget: Widget object with ticker, config (option_type, strike, expiration, date_range)
 * - onCrosshairMove: Callback for synchronized crosshair
 * - sharedCrosshair: Date string for shared crosshair position
 * - isLinked: Whether crosshair is linked across widgets
 */
function OptionsChartWidget({ widget, onCrosshairMove, sharedCrosshair, isLinked, crossChartData, onCrosshairData, livePrice, liveTimestamp }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparePoints, setComparePoints] = useState({ pointA: null, pointB: null });

  // Refs for live data to avoid adding to loadChartData's dependency array
  const livePriceRef = useRef(livePrice);
  const liveTimestampRef = useRef(liveTimestamp);
  useEffect(() => { livePriceRef.current = livePrice; }, [livePrice]);
  useEffect(() => { liveTimestampRef.current = liveTimestamp; }, [liveTimestamp]);

  // Extract config from widget
  const { ticker, config = {} } = widget;
  const { option_type = 'call', strike, expiration, date_range = '3m' } = config;

  // Data sanitization helper
  const sanitizeChartData = (data) => {
    if (!data || !Array.isArray(data)) {
      console.warn('[OptionsChartWidget] Invalid data format', data);
      return [];
    }

    return data.map((item, index) => {
      return {
        date: item?.date || `unknown-${index}`,
        open: typeof item?.open === 'number' && !isNaN(item.open) && isFinite(item.open) ? item.open : null,
        high: typeof item?.high === 'number' && !isNaN(item.high) && isFinite(item.high) ? item.high : null,
        low: typeof item?.low === 'number' && !isNaN(item.low) && isFinite(item.low) ? item.low : null,
        close: typeof item?.close === 'number' && !isNaN(item.close) && isFinite(item.close) ? item.close : null,
        volume: typeof item?.volume === 'number' && !isNaN(item.volume) && isFinite(item.volume) ? item.volume : 0,
      };
    }).filter(item => item.close !== null);
  };

  // Pad the start of the data set with flat-priced synthetic points covering
  // the gap between the requested range start and the first available data
  // point. Used when a freshly-issued contract is younger than the selected
  // time range — keeps the x-axis spanning the full range so the user sees
  // "this contract did not exist yet" as a flat line at its inception price
  // rather than as a compressed plot crammed against the right edge.
  const padHistoryToRange = (data, dateRange) => {
    if (!Array.isArray(data) || data.length === 0) return data;
    const monthsByRange = { '1m': 1, '3m': 3, '6m': 6, '1y': 12, '2y': 24, '5y': 60 };
    const months = monthsByRange[dateRange];
    if (!months) return data;

    const rangeStart = new Date();
    rangeStart.setMonth(rangeStart.getMonth() - months);
    rangeStart.setHours(0, 0, 0, 0);

    const firstActualDate = new Date(data[0].date);
    if (isNaN(firstActualDate.getTime())) return data;
    firstActualDate.setHours(0, 0, 0, 0);
    if (firstActualDate <= rangeStart) return data; // no gap, nothing to pad

    const firstClose = data[0].close;
    if (firstClose == null || isNaN(firstClose)) return data;

    // One synthetic point per calendar day from rangeStart up to (but not
    // including) firstActualDate. Recharts category-axis treats array index
    // as x-position, so daily points keep date proportions honest.
    const pad = [];
    const cursor = new Date(rangeStart);
    while (cursor < firstActualDate) {
      const yyyy = cursor.getFullYear();
      const mm = String(cursor.getMonth() + 1).padStart(2, '0');
      const dd = String(cursor.getDate()).padStart(2, '0');
      pad.push({
        date: `${yyyy}-${mm}-${dd}`,
        open: firstClose,
        high: firstClose,
        low: firstClose,
        close: firstClose,
        volume: 0,
        _isPadded: true,
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return [...pad, ...data];
  };

  // Load chart data
  const loadChartData = useCallback(async () => {
    if (!ticker || !strike || !expiration) {
      setError('Missing contract details');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[OptionsChartWidget] Loading data for ${ticker} ${option_type} $${strike} ${expiration}`);
      const response = await getOptionsHistoricalData(ticker, option_type, strike, expiration, date_range);

      console.log('[OptionsChartWidget] Response:', response);

      const rawData = response?.data || response || [];
      console.log(`[OptionsChartWidget] Raw data points: ${rawData.length}`);

      const sanitizedData = sanitizeChartData(rawData);
      console.log(`[OptionsChartWidget] Sanitized data points: ${sanitizedData.length}`);

      if (sanitizedData.length === 0) {
        setError('No trading data available for this contract');
      } else {
        // Pad short-history contracts with a flat line back to the requested
        // range start. No-op when data already covers the range or when the
        // range token doesn't support padding (e.g. 'All').
        const paddedData = padHistoryToRange(sanitizedData, date_range);

        // Append or update live data point if available
        const lp = livePriceRef.current;
        const lt = liveTimestampRef.current;
        if (lp && !isNaN(lp) && paddedData.length > 0) {
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          const lastDate = paddedData[paddedData.length - 1]?.date;
          if (lastDate < todayStr) {
            // Historical data is behind — append a new live point
            paddedData.push({
              date: todayStr,
              open: null, high: null, low: null,
              close: lp,
              volume: null,
              isLivePoint: true,
              liveTimestamp: lt,
            });
          } else if (lastDate === todayStr) {
            // Today's bar exists but may be stale EOD — update with live price
            paddedData[paddedData.length - 1] = {
              ...paddedData[paddedData.length - 1],
              close: lp,
              isLivePoint: true,
              liveTimestamp: lt,
            };
          }
        }
        setChartData(paddedData);
      }
    } catch (err) {
      console.error('[OptionsChartWidget] Error loading data:', err);
      setError(`Failed to load options data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [ticker, option_type, strike, expiration, date_range]);

  // Load data when props change
  useEffect(() => {
    loadChartData();
  }, [loadChartData]);

  // Memoize event handlers
  const handleMouseMove = useCallback((data) => {
    if (!isLinked || !data || !data.activeLabel) return;
    if (onCrosshairMove) onCrosshairMove(data.activeLabel);
    if (onCrosshairData && data.activePayload?.[0]?.payload) {
      onCrosshairData(data.activePayload[0].payload);
    }
  }, [isLinked, onCrosshairMove, onCrosshairData]);

  const handleMouseLeave = useCallback(() => {
    if (isLinked) {
      if (onCrosshairMove) onCrosshairMove(null);
      if (onCrosshairData) onCrosshairData(null);
    }
  }, [isLinked, onCrosshairMove, onCrosshairData]);

  // Click handler for point comparison
  const handleChartClick = useCallback((data) => {
    console.log('[OptionsChartWidget] Chart clicked:', data);
    if (!data || !data.activePayload || data.activePayload.length === 0) {
      console.log('[OptionsChartWidget] No active payload - click may have been outside data area');
      return;
    }

    const clickedData = data.activePayload[0].payload;
    console.log('[OptionsChartWidget] Clicked data point:', clickedData);
    const clickedPoint = {
      date: clickedData.date,
      price: clickedData.close,
      index: chartData.findIndex(d => d.date === clickedData.date)
    };

    setComparePoints(prev => {
      // If both points are set, clear and start fresh
      if (prev.pointA && prev.pointB) {
        console.log('[OptionsChartWidget] Clearing both points');
        return { pointA: null, pointB: null };
      }
      // If only Point A is set, set Point B
      if (prev.pointA) {
        console.log('[OptionsChartWidget] Setting Point B:', clickedPoint);
        return { ...prev, pointB: clickedPoint };
      }
      // Otherwise, set Point A
      console.log('[OptionsChartWidget] Setting Point A:', clickedPoint);
      return { pointA: clickedPoint, pointB: null };
    });
  }, [chartData]);

  // When the OTHER chart moves the shared crosshair, look up our data at that date
  // and report it via onCrosshairData so the other chart's tooltip can display it.
  useEffect(() => {
    if (!isLinked || !sharedCrosshair || !onCrosshairData || chartData.length === 0) return;
    const match = chartData.find(d => d.date === sharedCrosshair);
    if (match) {
      onCrosshairData(match);
    }
  }, [isLinked, sharedCrosshair, chartData, onCrosshairData]);

  // Clear comparison points when date range changes
  useEffect(() => {
    setComparePoints({ pointA: null, pointB: null });
  }, [date_range]);

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <StandardLoader />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-background p-4">
        <p className="text-red-500 text-center mb-2 text-sm">{error}</p>
        <button
          onClick={loadChartData}
          className="text-xs text-blue-400 hover:text-blue-300 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  // No data state
  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <p className="text-gray-400 text-sm">No trading data available</p>
      </div>
    );
  }

  // Calculate price domain with padding
  const validPrices = chartData.map(d => d.close).filter(p => p !== null && !isNaN(p));
  let priceMin = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  let priceMax = validPrices.length > 0 ? Math.max(...validPrices) : 100;

  // Handle edge case where all prices are the same
  if (priceMin === priceMax) {
    priceMax = priceMin + 0.1;
  }

  // Add padding
  const pricePadding = (priceMax - priceMin) * 0.15;
  priceMin = Math.max(0, priceMin - pricePadding);
  priceMax = priceMax + pricePadding;

  // Calculate % change from first to last
  const firstPrice = chartData[0]?.close;
  const lastPrice = chartData[chartData.length - 1]?.close;
  const priceChange = firstPrice && lastPrice ? ((lastPrice - firstPrice) / firstPrice * 100) : 0;
  const isPositive = priceChange >= 0;

  // Calculate displayed % change (comparison points or default)
  const getDisplayedPriceChange = () => {
    const { pointA, pointB } = comparePoints;

    // If both comparison points are set, use those
    if (pointA && pointB) {
      const startPrice = pointA.price;
      const endPrice = pointB.price;
      if (startPrice && endPrice) {
        return ((endPrice - startPrice) / startPrice * 100);
      }
    }

    // Default: use first-to-last of timeframe
    return priceChange;
  };

  const displayedPriceChange = getDisplayedPriceChange();
  const displayIsPositive = displayedPriceChange >= 0;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !Array.isArray(payload) || payload.length === 0) {
      return null;
    }

    try {
      // Parse YYYY-MM-DD directly to avoid timezone offset issues with new Date()
      let formattedDate = label || 'Unknown date';
      if (label && label.includes('-')) {
        const [y, m, d] = label.split('-');
        formattedDate = `${parseInt(m)}/${parseInt(d)}/${y}`;
      }

      const getValue = (dataKey) => {
        const item = payload.find(p => p?.dataKey === dataKey);
        return item?.value;
      };

      const closeValue = getValue('close');
      const highValue = getValue('high');
      const lowValue = getValue('low');
      const volumeValue = getValue('volume');

      // When linked, look up cross-chart data (underlying) at the same date
      const cc = isLinked && crossChartData ? crossChartData : null;

      return (
        <div className="bg-gray-900 border border-gray-700 p-2 rounded text-xs shadow-lg">
          <p className="text-gray-400 mb-1">{formattedDate}</p>
          {payload[0]?.payload?.isLivePoint && (
            <p className="text-yellow-400 text-[10px] font-semibold mb-1">
              {payload[0].payload.liveTimestamp
                ? `As of ${new Date(payload[0].payload.liveTimestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`
                : 'LIVE'}
            </p>
          )}
          {closeValue !== undefined && (
            <p className={isPositive ? 'text-green-400' : 'text-red-400'}>
              Premium: ${closeValue?.toFixed(2) || 'N/A'}
            </p>
          )}
          {highValue !== undefined && (
            <p className="text-gray-300 text-xs">
              High: ${highValue?.toFixed(2)} / Low: ${lowValue?.toFixed(2)}
            </p>
          )}
          {volumeValue !== undefined && volumeValue > 0 && (
            <p className="text-gray-500 text-xs">
              Volume: {volumeValue.toLocaleString()}
            </p>
          )}
          {/* Cross-chart data: underlying price + all indicators */}
          {cc && (
            <div className="border-t border-gray-700 mt-1.5 pt-1.5">
              {cc.close != null && <p className="text-green-400">Price: ${cc.close.toFixed(2)}</p>}
              {cc.bb_upper != null && <p className="text-blue-400">BB: ${cc.bb_lower?.toFixed(2)}-${cc.bb_upper.toFixed(2)}</p>}
              {cc.sma_50 != null && <p className="text-yellow-400">SMA50: ${cc.sma_50.toFixed(2)}</p>}
              {cc.sma_200 != null && <p className="text-pink-400">SMA200: ${cc.sma_200.toFixed(2)}</p>}
              {cc.sma_720 != null && <p className="text-pink-300">SMA720: ${cc.sma_720.toFixed(2)}</p>}
              {cc.ema != null && <p className="text-teal-400">EMA: ${cc.ema.toFixed(2)}</p>}
              {cc.vwap != null && <p className="text-indigo-400">VWAP: ${cc.vwap.toFixed(2)}</p>}
              {cc.macd != null && <p className="text-cyan-400">MACD: {cc.macd.toFixed(3)}</p>}
              {cc.macd_signal != null && <p className="text-orange-400">Signal: {cc.macd_signal.toFixed(3)}</p>}
              {cc.macd_histogram != null && <p className="text-gray-400">Hist: {cc.macd_histogram.toFixed(3)}</p>}
              {cc.rsi != null && <p className="text-purple-400">RSI: {cc.rsi.toFixed(1)}</p>}
              {cc.volume != null && cc.volume > 0 && <p className="text-gray-500">Stock Vol: {cc.volume.toLocaleString()}</p>}
              {cc.volume_ma != null && <p className="text-gray-400">Vol MA: {cc.volume_ma.toLocaleString()}</p>}
              {cc.ac != null && <p style={{ color: cc.ac >= 0 ? '#10b981' : '#ef4444' }}>AC: {cc.ac.toFixed(3)}</p>}
            </div>
          )}
        </div>
      );
    } catch (err) {
      console.error('[OptionsChartWidget] Tooltip render error:', err);
      return null;
    }
  };

  // Format contract description
  const contractDesc = `${ticker} $${strike} ${option_type?.toUpperCase()} ${expiration}`;

  return (
    <div className="h-full flex flex-col bg-gray-950">
      {/* Chart Area - Stacked Panels */}
      <div className="flex-1 flex flex-col p-2 gap-1">
        {/* Price Panel - stopPropagation on mouseDown prevents grid drag but allows chart clicks */}
        <div
          className="flex-[3]"
          style={{ cursor: 'crosshair' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleChartClick}
              margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
            >
              <defs>
                <linearGradient id={`premiumGradient-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={isPositive ? '#10b981' : '#ef4444'}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={isPositive ? '#10b981' : '#ef4444'}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="1 3" stroke="#1f2937" vertical={false} />

              <XAxis
                dataKey="date"
                stroke="#4b5563"
                tick={false}
                axisLine={{ stroke: '#1f2937' }}
              />

              <YAxis
                stroke="#4b5563"
                tick={{ fontSize: 10, fill: '#6b7280' }}
                domain={[priceMin, priceMax]}
                tickFormatter={(value) => `$${value?.toFixed(2) || '0'}`}
              />

              <Tooltip content={<CustomTooltip />} />

              {/* Area fill under the line */}
              <Area
                type="monotone"
                dataKey="close"
                stroke="none"
                fill={`url(#premiumGradient-${widget.id})`}
                connectNulls={true}
              />

              {/* Main Premium Line */}
              <Line
                type="monotone"
                dataKey="close"
                stroke={isPositive ? '#10b981' : '#ef4444'}
                strokeWidth={2}
                dot={(props) => {
                  const { cx, cy, payload, index } = props;
                  if (!payload?.isLivePoint) return <circle key={`dot-${index}`} r={0} />;
                  return (
                    <circle
                      key={`dot-${index}`}
                      cx={cx} cy={cy} r={5}
                      fill={isPositive ? '#10b981' : '#ef4444'}
                      stroke="#ffffff"
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{ r: 4, fill: isPositive ? '#10b981' : '#ef4444' }}
                connectNulls={true}
              />

              {/* Synchronized crosshair */}
              {isLinked && sharedCrosshair && (
                <ReferenceLine
                  x={sharedCrosshair}
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
              )}

              {/* Point A marker (blue) */}
              {comparePoints.pointA && (
                <>
                  <ReferenceLine
                    x={comparePoints.pointA.date}
                    stroke="#3b82f6"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                  />
                  <ReferenceDot
                    x={comparePoints.pointA.date}
                    y={comparePoints.pointA.price}
                    r={6}
                    fill="#3b82f6"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                </>
              )}

              {/* Point B marker (amber) */}
              {comparePoints.pointB && (
                <>
                  <ReferenceLine
                    x={comparePoints.pointB.date}
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                  />
                  <ReferenceDot
                    x={comparePoints.pointB.date}
                    y={comparePoints.pointB.price}
                    r={6}
                    fill="#f59e0b"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Volume Panel */}
        <div className="flex-[1] border-t border-gray-800">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              margin={{ top: 5, right: 10, left: 10, bottom: 24 }}
            >
              <CartesianGrid strokeDasharray="1 3" stroke="#1f2937" vertical={false} />

              <XAxis
                dataKey="date"
                stroke="#4b5563"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                minTickGap={40}
                interval="preserveStartEnd"
                tickFormatter={(value) => {
                  try {
                    if (value && value.includes('-')) {
                      const [, m, d] = value.split('-');
                      return `${parseInt(m)}/${parseInt(d)}`;
                    }
                  } catch (e) {}
                  return '';
                }}
              />

              <YAxis
                stroke="#4b5563"
                tick={{ fontSize: 10, fill: '#6b7280' }}
                domain={['auto', 'auto']}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                width={40}
              />

              <Tooltip content={<CustomTooltip />} />

              <Bar
                dataKey="volume"
                fill="#6b7280"
                opacity={0.6}
              />

              {/* Synchronized crosshair */}
              {isLinked && sharedCrosshair && (
                <ReferenceLine
                  x={sharedCrosshair}
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Chart Info Footer */}
      <div className="px-3 py-2 bg-gray-900 border-t border-gray-800 flex items-center justify-between">
        <div className="text-gray-400 text-xs flex items-center gap-2">
          <span className={option_type === 'call' ? 'text-green-400' : 'text-red-400'}>
            {option_type === 'call' ? '📈' : '📉'}
          </span>
          <span>{contractDesc}</span>
          {/* Comparison mode indicator */}
          {(comparePoints.pointA || comparePoints.pointB) && (
            <span className="ml-2 text-blue-400 text-xs">
              {comparePoints.pointA && comparePoints.pointB
                ? '• Comparing 2 points (click to clear)'
                : '• Click to set 2nd point'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className={`font-medium ${displayIsPositive ? 'text-green-400' : 'text-red-400'}`}>
            {displayIsPositive ? '+' : ''}{displayedPriceChange.toFixed(2)}%
          </span>
          <span className="text-gray-600">
            {comparePoints.pointA && comparePoints.pointB
              ? 'Selected'
              : date_range?.toUpperCase() || '3M'}
          </span>
        </div>
      </div>
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export default memo(OptionsChartWidget, (prevProps, nextProps) => {
  return (
    prevProps.widget?.id === nextProps.widget?.id &&
    prevProps.widget?.ticker === nextProps.widget?.ticker &&
    prevProps.isLinked === nextProps.isLinked &&
    prevProps.sharedCrosshair === nextProps.sharedCrosshair &&
    prevProps.crossChartData === nextProps.crossChartData &&
    prevProps.livePrice === nextProps.livePrice &&
    JSON.stringify(prevProps.widget?.config) === JSON.stringify(nextProps.widget?.config)
  );
});
